import React, { useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import axiosInstance from '../../../config/axios.config';

const InfoItem = ({ label, value, fullWidth = false, highlight = false }) => (
    <div className={`flex flex-column gap-1 ${fullWidth ? 'col-12' : 'col-12 sm:col-6 lg:col-4'}`}>
        <span className="text-500 text-xs font-medium">{label}</span>
        <span className={`text-sm ${highlight ? 'text-primary font-semibold' : 'text-900 font-medium'}`}>
            {value || '—'}
        </span>
    </div>
);

const Section = ({ icon, title, children }) => (
    <div className="mb-4">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">
            {children}
        </div>
    </div>
);

const extractPanResponse = (reportData) => {
    if (!reportData) return { panResponse: null, errorMessage: null };
    if (reportData.success === false) {
        return {
            panResponse: reportData.response || reportData.data || {},
            errorMessage: reportData.message || 'PAN verification failed',
        };
    }
    if (reportData.report_json?.data?.response) return { panResponse: reportData.report_json.data.response, errorMessage: null };
    if (reportData.data?.response) return { panResponse: reportData.data.response, errorMessage: null };
    if (reportData.success && reportData.response?.response) return { panResponse: reportData.response.response, errorMessage: null };
    if (reportData.api_response?.response?.response) return { panResponse: reportData.api_response.response.response, errorMessage: null };
    if (reportData.response?.response) return { panResponse: reportData.response.response, errorMessage: null };
    if (reportData.response) return { panResponse: reportData.response, errorMessage: null };
    return { panResponse: reportData, errorMessage: null };
};

const resolveDownloadLink = (reportData, pdfDownloadUrl) =>
    pdfDownloadUrl ||
    reportData?.pdf_url ||
    reportData?.pdf_download_url ||
    reportData?.download_link ||
    reportData?.download_url ||
    reportData?.data?.pdf_url ||
    reportData?.data?.pdf_download_url ||
    reportData?.data?.download_link ||
    reportData?.response?.pdf_download_url ||
    reportData?.api_response?.pdf_download_url ||
    reportData?.report_json?.pdf_download_url;

const PANDetailsOutputComponent = ({ reportData, serviceInfo, onDownloadPDF, pdfDownloadUrl, hideCredits, isSharedView }) => {
    const toast = useRef(null);
    const shouldHideCredits = hideCredits || isSharedView;

    if (!reportData) {
        return (
            <Card className="text-center">
                <i className="pi pi-info-circle pi-3x text-primary mb-3" aria-hidden="true" />
                <p className="text-base text-700 m-0">No PAN verification data available</p>
            </Card>
        );
    }

    if (reportData?.code === '1004') {
        return (
            <Card className="text-center">
                <i className="pi pi-search pi-3x text-orange-500 mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">No records found</h3>
                <p className="text-600 text-sm m-0">
                    No credit bureau records were found for the provided details. Please enter valid details.
                </p>
            </Card>
        );
    }

    const { panResponse, errorMessage: extractedError } = extractPanResponse(reportData);
    const errorMessage =
        extractedError ||
        (panResponse?.error || panResponse?.statusCode === 'error'
            ? panResponse?.message || panResponse?.error || 'PAN verification failed'
            : null);

    if (errorMessage && (!panResponse || Object.keys(panResponse).length === 0)) {
        return (
            <Card className="text-center">
                <i className="pi pi-times-circle pi-3x text-red-500 mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">PAN verification failed</h3>
                <p className="text-600 text-sm m-0 mb-3">{errorMessage}</p>
                <Button
                    label="Try again"
                    icon="pi pi-refresh"
                    severity="secondary"
                    outlined
                    size="small"
                    onClick={() => window.location.reload()}
                />
            </Card>
        );
    }

    const remainingBalance = reportData?.data?.remaining_balance ?? reportData?.remaining_balance;
    const downloadLink = resolveDownloadLink(reportData, pdfDownloadUrl);
    const pdfPasswordHint =
        reportData?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        reportData?.response?.pdf_password_hint ||
        reportData?.api_response?.pdf_password_hint ||
        reportData?.report_json?.pdf_password_hint;

    const handleDownload = async () => {
        if (onDownloadPDF && typeof onDownloadPDF === 'function') {
            onDownloadPDF();
            return;
        }
        if (!downloadLink) {
            toast.current?.show({
                severity: 'warn',
                summary: 'PDF unavailable',
                detail: 'Download URL is not available for this report',
                life: 3000,
            });
            return;
        }

        if (downloadLink.startsWith('http')) {
            window.open(downloadLink, '_blank');
            return;
        }

        try {
            const urlObj = new URL(downloadLink, window.location.origin);
            const res = await axiosInstance.get(urlObj.pathname, {
                responseType: 'blob',
                headers: { Accept: 'application/pdf' },
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `pan_verification_${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.current?.show({ severity: 'success', summary: 'Downloaded', detail: 'PDF saved', life: 2000 });
        } catch (error) {
            console.error('PAN PDF download failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const detail =
                status === 404 ? 'Report not found. Please regenerate the report first.' :
                status === 400 ? 'No data available for PDF generation.' :
                status === 500 ? 'Internal server error. Please try again later.' :
                error.response?.data?.message || 'PDF download failed';
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail, life: 3500 });
        }
    };

    const aadhaarLinked = !!panResponse?.aadhaarSeedingStatus;
    const hasAddress = !!(panResponse?.address || panResponse?.city || panResponse?.state || panResponse?.pincode || panResponse?.country);

    return (
        <>
            <Toast ref={toast} />

            {/* Sub-header */}
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-900 m-0">PAN Verification Report</h2>
                {!shouldHideCredits && (
                    <Tag
                        severity="success"
                        icon="pi pi-wallet"
                        value={`Remaining: ${remainingBalance ?? serviceInfo?.available_credits ?? 'N/A'}`}
                        className="text-sm"
                    />
                )}
            </div>

            {errorMessage && (
                <Message severity="warn" text={errorMessage} className="w-full mb-3" />
            )}

            <Card>
                <Section icon="pi-user" title="Personal details">
                    <InfoItem label="Full name" value={panResponse?.name} />
                    {panResponse?.firstName && <InfoItem label="First name" value={panResponse.firstName} />}
                    {panResponse?.middleName && <InfoItem label="Middle name" value={panResponse.middleName} />}
                    {panResponse?.lastName && <InfoItem label="Last name" value={panResponse.lastName} />}
                    <InfoItem label="Date of birth" value={panResponse?.dob} />
                    {panResponse?.gender && (
                        <InfoItem
                            label="Gender"
                            value={panResponse.gender === 'M' ? 'Male' : panResponse.gender === 'F' ? 'Female' : panResponse.gender}
                        />
                    )}
                    {panResponse?.typeOfHolder && <InfoItem label="Holder type" value={panResponse.typeOfHolder} />}
                </Section>

                <Section icon="pi-id-card" title="Identification & contact">
                    <InfoItem label="PAN number" value={panResponse?.pan} highlight />
                    <div className="flex flex-column gap-1 col-12 sm:col-6 lg:col-4">
                        <span className="text-500 text-xs font-medium">Aadhaar status</span>
                        <span className="text-sm">
                            <Tag
                                severity={aadhaarLinked ? 'success' : 'warning'}
                                icon={`pi ${aadhaarLinked ? 'pi-check-circle' : 'pi-exclamation-circle'}`}
                                value={aadhaarLinked ? 'Linked' : 'Not linked'}
                            />
                        </span>
                    </div>
                    {panResponse?.maskedAadhaar && <InfoItem label="Masked Aadhaar" value={panResponse.maskedAadhaar} />}
                    {panResponse?.lastFourDigit && <InfoItem label="Last 4 digits" value={panResponse.lastFourDigit} />}
                    {panResponse?.mobile_no && <InfoItem label="Mobile number" value={panResponse.mobile_no} />}
                    {panResponse?.email && <InfoItem label="Email address" value={panResponse.email} />}
                </Section>

                {hasAddress && (
                    <Section icon="pi-home" title="Address information">
                        {panResponse?.address && <InfoItem label="Complete address" value={panResponse.address} fullWidth />}
                        {panResponse?.city && <InfoItem label="City" value={panResponse.city} />}
                        {panResponse?.state && <InfoItem label="State" value={panResponse.state} />}
                        {panResponse?.pincode && <InfoItem label="Pincode" value={panResponse.pincode} />}
                        {panResponse?.country && <InfoItem label="Country" value={panResponse.country} />}
                    </Section>
                )}

                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mt-2">
                    {pdfPasswordHint ? (
                        <Message severity="info" text={`PDF password hint: ${pdfPasswordHint}`} />
                    ) : <span />}
                    <Button
                        label="Download PDF"
                        icon="pi pi-download"
                        size="small"
                        disabled={!downloadLink && !onDownloadPDF}
                        onClick={handleDownload}
                    />
                </div>
            </Card>
        </>
    );
};

export default PANDetailsOutputComponent;
